<script lang="ts">
  import { onMount } from 'svelte';
  import type { TimeSeriesData } from '../lib/types';
  import { timeSeriesApi } from '../lib/api';
  import { toString } from '../lib/id-utils';
  import SeriesSelectionModal from './SeriesSelectionModal.svelte';
  
  // Props
  export let timeseriesId: string | number;
  export let timeseriesData: TimeSeriesData | null = null;
  export let height: number = 400;
  
  // We're not using the event dispatcher but might need it in the future
  
  // State
  let loading: boolean = false;
  let error: string = '';
  let plotType: 'line' | 'bar' | 'scatter' = 'line';
  let includeTrend: boolean = false;
  let includeStats: boolean = true;
  let normalize: boolean = false;
  let plotImage: string = '';
  let histogramImage: string = '';
  let showHistogram: boolean = false;
  let compareModeActive: boolean = false;
  // Use string[] for comparisonSeries to ensure consistency
  let comparisonSeries: string[] = [];
  let comparisonImage: string = '';
  
  // Fetch plot image
  async function fetchPlot() {
    if (!timeseriesId) return;
    
    try {
      loading = true;
      error = '';
      
      const params = new URLSearchParams({
        width: '800',
        height: height.toString(),
        plot_type: plotType,
        include_trend: includeTrend.toString(),
        include_stats: includeStats.toString()
      });
      
      const response = await fetch(`/api/visualization/${timeseriesId}/plot?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch plot: ${response.statusText}`);
      }
      
      const data = await response.json();
      plotImage = data.image;
      
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to fetch plot';
    } finally {
      loading = false;
    }
  }
  
  // Fetch histogram
  async function fetchHistogram() {
    if (!timeseriesId) return;
    
    try {
      loading = true;
      error = '';
      
      const params = new URLSearchParams({
        width: '800',
        height: height.toString(),
        bins: '20',
        include_stats: includeStats.toString()
      });
      
      const response = await fetch(`/api/visualization/${timeseriesId}/histogram?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch histogram: ${response.statusText}`);
      }
      
      const data = await response.json();
      histogramImage = data.image;
      
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to fetch histogram';
    } finally {
      loading = false;
    }
  }
  
  // Fetch comparison graph
  async function fetchComparison() {
    if (comparisonSeries.length === 0) {
      comparisonSeries = [toString(timeseriesId)];
    }
    
    if (comparisonSeries.length === 0) return;
    
    try {
      loading = true;
      error = '';
      
      const params = new URLSearchParams();
      comparisonSeries.forEach(id => params.append('ids', id));
      params.append('width', '800');
      params.append('height', height.toString());
      params.append('normalize', normalize.toString());
      
      const response = await fetch(`/api/visualization/compare?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch comparison: ${response.statusText}`);
      }
      
      const data = await response.json();
      comparisonImage = data.image;
      
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to fetch comparison';
    } finally {
      loading = false;
    }
  }
  
  // Handle visualization options change
  $: if (timeseriesId && !compareModeActive && !showHistogram) {
    fetchPlot();
  }
  
  $: if (timeseriesId && showHistogram) {
    fetchHistogram();
  }
  
  // Function to add or remove comparison series
  function toggleComparison(id: string) {
    const index = comparisonSeries.indexOf(id);
    if (index > -1) {
      comparisonSeries.splice(index, 1);
      comparisonSeries = [...comparisonSeries];
    } else {
      if (comparisonSeries.length < 5) {
        comparisonSeries = [...comparisonSeries, id];
      }
    }
    
    // Fetch updated comparison if active
    if (compareModeActive) {
      fetchComparison();
    }
  }
  
  // Toggle comparison mode
  function toggleComparisonMode() {
    compareModeActive = !compareModeActive;
    
    if (compareModeActive) {
      const tsIdString = toString(timeseriesId);
      if (!comparisonSeries.includes(tsIdString)) {
        comparisonSeries = [tsIdString, ...comparisonSeries];
      }
      fetchComparison();
    }
  }
  
  onMount(() => {
    // Initialize with default plot
    if (timeseriesId) {
      fetchPlot();
    }
  });
  
  // Export list of available time series for comparison
  function exportAvailableSeries() {
    loadAvailableSeries();
  }
  
  // Update available series list from parent
  export function updateAvailableSeries(series: {id: string, name: string}[]) {
    availableSeries = series.filter(s => s.id !== timeseriesId);
  }
  
  // Available series for comparison
  let availableSeries: {id: string, name: string}[] = [];
  
  // Modal state
  let showSeriesModal: boolean = false;
  let selectedSeriesIds: string[] = [];
  
  // Handle selected series from modal
  function handleSeriesSelected(event: CustomEvent) {
    const { ids } = event.detail;
    comparisonSeries = ids;
    if (compareModeActive && comparisonSeries.length > 0) {
      fetchComparison();
    }
  }
  
  // Load available series
  async function loadAvailableSeries() {
    try {
      loading = true;
      error = '';
      
      const response = await timeSeriesApi.getAll(0, 100);
      availableSeries = response.data
        .filter(s => s.id !== timeseriesId)
        .map(s => ({ id: toString(s.id), name: s.name }));
      
      showSeriesModal = true;
      selectedSeriesIds = comparisonSeries.slice();
      
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load time series';
    } finally {
      loading = false;
    }
  }
</script>

<div class="visualization-panel card">
  <div class="card-header d-flex justify-content-between align-items-center">
    <h5 class="mb-0">Визуализация</h5>
    <div class="btn-group">
      <button 
        class="btn btn-sm {showHistogram ? 'btn-outline-primary' : 'btn-primary'}" 
        on:click={() => { showHistogram = false; compareModeActive = false; }}
        disabled={!timeseriesId}
      >
        <i class="bi bi-graph-up me-1"></i>
        График
      </button>
      <button 
        class="btn btn-sm {showHistogram ? 'btn-primary' : 'btn-outline-primary'}" 
        on:click={() => { showHistogram = true; compareModeActive = false; }}
        disabled={!timeseriesId}
      >
        <i class="bi bi-bar-chart me-1"></i>
        Гистограмма
      </button>
      <button 
        class="btn btn-sm {compareModeActive ? 'btn-primary' : 'btn-outline-primary'}" 
        on:click={toggleComparisonMode}
        disabled={!timeseriesId}
      >
        <i class="bi bi-graph-up-arrow me-1"></i>
        Сравнение
      </button>
    </div>
  </div>
  
  <div class="card-body">
    {#if error}
      <div class="alert alert-danger" role="alert">
        {error}
      </div>
    {/if}
    
    <!-- Plot Options -->
    {#if !showHistogram && !compareModeActive}
      <div class="mb-3">
        <div class="row g-2">
          <div class="col-md-4">
            <label class="form-label">Тип графика</label>
            <select class="form-select" bind:value={plotType}>
              <option value="line">Линейный</option>
              <option value="bar">Столбчатый</option>
              <option value="scatter">Точечный</option>
            </select>
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check me-3">
              <input class="form-check-input" type="checkbox" id="includeTrend" bind:checked={includeTrend}>
              <label class="form-check-label" for="includeTrend">
                Тренд
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="includeStats" bind:checked={includeStats}>
              <label class="form-check-label" for="includeStats">
                Статистика
              </label>
            </div>
          </div>
          <div class="col-md-4 d-flex justify-content-end align-items-end">
            <button class="btn btn-primary" on:click={fetchPlot} disabled={loading}>
              <i class="bi bi-arrow-repeat me-1"></i>
              Обновить
            </button>
          </div>
        </div>
      </div>
    {/if}
    
    <!-- Histogram options -->
    {#if showHistogram}
      <div class="mb-3">
        <div class="row g-2">
          <div class="col-md-8">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="includeStatsHist" bind:checked={includeStats}>
              <label class="form-check-label" for="includeStatsHist">
                Статистика
              </label>
            </div>
          </div>
          <div class="col-md-4 d-flex justify-content-end">
            <button class="btn btn-primary" on:click={fetchHistogram} disabled={loading}>
              <i class="bi bi-arrow-repeat me-1"></i>
              Обновить
            </button>
          </div>
        </div>
      </div>
    {/if}
    
    <!-- Comparison options -->
    {#if compareModeActive}
      <div class="mb-3">
        <div class="row g-2 mb-2">
          <div class="col-md-8">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="normalize" bind:checked={normalize}>
              <label class="form-check-label" for="normalize">
                Нормализовать значения (0-1)
              </label>
            </div>
          </div>
          <div class="col-md-4 d-flex justify-content-end">
            <button class="btn btn-primary" on:click={fetchComparison} disabled={loading || comparisonSeries.length === 0}>
              <i class="bi bi-arrow-repeat me-1"></i>
              Обновить
            </button>
          </div>
        </div>
        
        <!-- Series selection -->
        <div class="series-selection mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Выбранные ряды:</h6>
            <button class="btn btn-sm btn-outline-primary" on:click={exportAvailableSeries}>
              <i class="bi bi-plus-circle me-1"></i>
              Добавить ряд
            </button>
          </div>
          
          <div class="selected-series">
            {#if comparisonSeries.length === 0}
              <div class="text-muted">Нет выбранных рядов</div>
            {:else}
              <div class="list-group">
                {#each comparisonSeries as seriesId}
                  <div class="list-group-item d-flex justify-content-between align-items-center">
                    <span>{seriesId === timeseriesId ? (timeseriesData?.name || 'Текущий ряд') : 
                          availableSeries.find(s => s.id === seriesId)?.name || seriesId}</span>
                    <button class="btn btn-sm btn-outline-danger" on:click={() => toggleComparison(seriesId)}>
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </div>
      </div>
    {/if}
    
    <!-- Visualization result -->
    <div class="visualization-result">
      {#if loading}
        <div class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3">Генерация визуализации...</p>
        </div>
      {:else if !timeseriesId}
        <div class="text-center py-5">
          <p class="text-muted">Выберите временной ряд для визуализации</p>
        </div>
      {:else if showHistogram && histogramImage}
        <div class="text-center">
          <img src={histogramImage} alt="Histogram" class="img-fluid" />
        </div>
      {:else if compareModeActive && comparisonImage}
        <div class="text-center">
          <img src={comparisonImage} alt="Comparison" class="img-fluid" />
        </div>
      {:else if !showHistogram && !compareModeActive && plotImage}
        <div class="text-center">
          <img src={plotImage} alt="Plot" class="img-fluid" />
        </div>
      {:else}
        <div class="text-center py-5">
          <p class="text-muted">Нет данных для отображения</p>
        </div>
      {/if}
    </div>
  </div>
</div>

<!-- Series Selection Modal -->
<SeriesSelectionModal
  bind:show={showSeriesModal}
  excludeId={timeseriesId}
  multiSelect={true}
  bind:selectedIds={selectedSeriesIds}
  on:select={handleSeriesSelected}
  on:close={() => showSeriesModal = false}
/>

<style>
  .visualization-panel {
    margin-bottom: 1.5rem;
  }
  
  .visualization-result {
    min-height: 300px;
  }
  
  .series-selection {
    max-height: 200px;
    overflow-y: auto;
  }
</style>